import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, inject, OnInit, Output } from "@angular/core";
import { LanguageService } from "@app/language.service";
import { CountrySelectionMenuComponent } from "@components/locales-selection-menu/country-selection-menu/country-selection-menu.component";
import { LanguageSelectionMenuComponent } from "@components/locales-selection-menu/language-selection-menu/language-selection-menu.component";
import { ProductCountryService } from "@domain/product/product-country.service";
import { ProductLanguageService } from "@domain/product/product-language.service";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ButtonComponent } from "@odx/angular/components/button";
import { SwitchComponent } from "@odx/angular/components/switch";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, Observable, of, switchMap } from "rxjs";

@Component({
  selector: "app-locales-selection-menu",
  imports: [LanguageSelectionMenuComponent, ButtonComponent, CountrySelectionMenuComponent, AsyncPipe, SwitchComponent],
  templateUrl: "./locales-selection-menu.component.html",
  styleUrl: "./locales-selection-menu.component.scss",
  standalone: true,
})
export class LocalesSelectionMenuComponent implements OnInit {
  private languageService = inject(LanguageService);
  private userService = inject(UserService);
  private productCountryService = inject(ProductCountryService);
  private productLanguageService = inject(ProductLanguageService);
  private toastHelper = inject(ToastHelper);

  protected selectedCountry?: string;
  protected selectedLanguage?: string;
  protected selectedShowZrefPrices?: boolean;
  private currentCountry?: string;
  private currentLanguage?: string;
  private currentShowZrefPrices?: boolean;
  protected user$: Observable<User>;
  protected readonly BusinessScope = BusinessScope;

  @Output()
  private selectionChange: EventEmitter<any> = new EventEmitter<any>();

  constructor() {
    this.user$ = this.userService.user$;
  }

  ngOnInit() {
    this.user$.subscribe((user) => {
      this.currentCountry = user.country;
      this.currentLanguage = user.language;
      this.selectedCountry = user.country;
      this.selectedLanguage = user.language;
      this.currentShowZrefPrices = user.showZrefPrices;
    });
  }

  protected onClickSelectLanguage(selection: string) {
    this.selectedLanguage = selection;
  }

  protected onClickSelectCountry(selection: string) {
    this.selectedCountry = selection;
  }

  protected readonly zrefPricesLabel = $localize`:@@localesSelectionMenu.showZrefPrices:ZREF-Preise anzeigen`;

  protected onToggleZrefPrices() {
    const current = this.selectedShowZrefPrices ?? this.currentShowZrefPrices ?? false;
    this.selectedShowZrefPrices = !current;
  }

  protected async onSubmit() {
    const zrefChanged = this.selectedShowZrefPrices !== undefined && this.selectedShowZrefPrices !== this.currentShowZrefPrices;
    const countryChanged = this.selectedCountry !== undefined && this.selectedCountry !== this.currentCountry;
    const languageChanged = this.selectedLanguage !== undefined && this.selectedLanguage !== this.currentLanguage;

    if (!navigator.onLine) {
      if (zrefChanged) {
        this.userService.applyZrefPricesLocally(this.selectedShowZrefPrices!);
        this.currentShowZrefPrices = this.selectedShowZrefPrices;
        this.toastHelper.message(
          $localize`:@@localesSelectionMenu.zrefOfflineCached:ZREF-Einstellung gespeichert und wird synchronisiert, sobald die Verbindung wiederhergestellt ist`,
        );
        this.selectionChange.emit();
      } else {
        this.toastHelper.errorOffline();
      }
      return;
    }

    const countryUpdate$ = countryChanged ? this.updateCountry(this.selectedCountry!) : of(null);
    const languageUpdate$ = languageChanged ? this.updateLanguage(this.selectedLanguage!) : of(null);
    const zrefUpdate$ = zrefChanged ? this.userService.updateShowZrefPrices(this.selectedShowZrefPrices!) : of(null);

    countryUpdate$
      .pipe(
        switchMap(() => languageUpdate$),
        switchMap(() => zrefUpdate$),
      )
      .subscribe({
        next: () => {
          this.selectionChange.emit();
        },
        error: () => {
          this.toastHelper.error($localize`:@@localesSelectionMenu.submit.error:Einstellungen konnten nicht gespeichert werden`);
        },
      });
  }

  private updateLanguage(selectedLanguage: string) {
    if (selectedLanguage === this.currentLanguage) return of(null);
    return this.languageService.switchLanguage(selectedLanguage).pipe(
      catchError(() => {
        this.toastHelper.error($localize`:@@localesSelectionMenu.switchLanguage.error:Sprache konnte nicht gewechselt werden`);
        return of(null);
      }),
      switchMap(() => this.productLanguageService.selectLanguage(selectedLanguage)),
    );
  }

  private updateCountry(selectedCountry: string): Observable<any> {
    if (selectedCountry === this.currentCountry) return of(null);
    return this.userService.updateCountry(selectedCountry).pipe(
      catchError(() => {
        this.toastHelper.error($localize`:@@localesSelectionMenu.updateCountry.error:Land konnte nicht aktualisiert werden`);
        return of(null);
      }),
      switchMap(() => this.productCountryService.selectCountry(selectedCountry)),
    );
  }
}
